import { Autocomplete, Box, Card, Checkbox, Container, FormControl, Icon, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem, MenuList, Select, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField, Typography } from "@mui/material";
import DialogContext from "components/confirm-dialog-context";
import { CustomButton } from "components/roundedbutton/CustomButton";
import useCatchApiError, { Problem } from "hooks/useCatchApiError";
import useFlowRoles from "hooks/useFlowRoles";
import useShowProgress from "hooks/useShowProgress";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import { StatusPedido } from "pages/registra-venda/registra-venda.model";
import RegistraVendaService from "pages/registra-venda/registra-venda.service";
import Ellipsis from "pages/Venda/Ellipsis";
import CancelaCoisasDialog from "pages/Venda/Header/CancelaCoisasDialog";
import Data from "pages/Venda/Header/Data";
import TableCellWithTooltip from "pages/Venda/Header/TableCellWithTooltip";
import VendaService from "pages/Venda/venda.service";
import { useContext, useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { completeLoading, loadingWithOpt, resetLoading, Tipo } from "redux/features/blockuiSlice";
import { setDeposito, setTabelaPreco } from "redux/features/configuracoes";
import { resetConsignado } from "redux/features/consignadoSlice";
import { setPagamentos } from "redux/features/pagamentoSlice";
import { setStateByProps, setTransportador } from "redux/features/stateSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { customColors, theme } from "theme";
import { getEnumObjNameDescritivo } from "utils/enum-utils";
import { ColumnData, currencyOf, toLocalDateBackEndFriendly } from "utils/util";
import ConsignadoService from "../consignado.service";
import { ValidateSituacaoNotasSaidasDevolucao } from "../devolucao/ConsignadoDevolucao";
import { FlowConsignadoImpressaoDTO } from "../impressao/consignado-impressao.model";
import ConsignadoImpressaoService from "../impressao/consignado-impressao.service";
import ConsignadoPDF from "../impressao/ConsignadoPDF";
import { SituacaoConsignado, SituacaoConsignadoClass } from "../situacao-consignado.model";
import ConsignadoVisualizarDialog from "../visualizar/ConsignadoVisualizarDialog";
import { ConsignadoListLocalState, ConsignadoListTotalizadores, FlowConsignadoListFilterPayloadDTO, FlowConsignadoListResponseDTO } from "./consignado-list.model";


const initialState: ConsignadoListLocalState = {
    ordem: null,
    cliente: null,
    vendedor: null,
    clientesAc: [],
    vendedoresAc: [],
    emissaoInicio: new Date().toString(),
    emissaoFim: new Date().toString(),
    situacao: [
        SituacaoConsignado.ABERTO,
        SituacaoConsignado.PARCIAL,
        SituacaoConsignado.CONSIGNADO
    ],
    numeroDocumentoSaida: null,
    numeroDocumentoEntrada: null,
    itens: [],
    qtdValores: {
        quantidadeConsignada: 0,
        quantidadeDevolvida: 0,
        quantidadeVendida: 0,
        saldo: 0,
        valorConsignado: 0,
        valorDevolvido: 0,
        valorVendido: 0,
        valorSaldo: 0,
    },
    isLoading: false,
    totalizadores: {
        quantidade: 0,
        quantidadeConsignada: 0,
        quantidadeDevolvido: 0,
        saldo: 0,
        quantidadeVendida: 0,
        quantidadeCancelada: 0,
        valorAberto: 0,
        valorConsignado: 0,
    }
};

const ConsignadoList = () => {
    const isFirstRenderRef = useRef(true);
    const {
        canNovo,
        canEditar,
        canImprimir,
        canMovimentacoes,
        canCancelar,
        canDevolucaoAcoes,
        canGerarVendaAcoes,
    } = useFlowRoles().consignado;
    const navigate = useNavigate();
    const consignadoService = ConsignadoService();
    const consignadoImpressaoService = ConsignadoImpressaoService();
    const registraVendaService = RegistraVendaService();
    const vendaService = VendaService();

    const dialog = useContext(DialogContext);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const selectedItemref = useRef<{ item: FlowConsignadoListResponseDTO, situacao: SituacaoConsignadoClass; } | null>(null);
    const openMenuRow = Boolean(anchorEl);
    const [impressao, setImpressao] = useState(null as FlowConsignadoImpressaoDTO | null);
    const [showCancelarDialog, setShowCancelarDialog] = useState(false);
    const { showProgress, hideProgress } = useShowProgress();
    const { throwErrorMessage } = useCatchApiError();
    const { addError, addSuccess } = useSnackbarCustom();

    const dispatch = useAppDispatch();
    const [localState, setLocalState] = useState(initialState);
    const { caixaAberto } = useAppSelector(e => e.caixa.caixaAtual);
    const estabelecimentoSelecionado = useAppSelector(e => e.estabelecimentoSelecionado);

    const relatorioColumnsLabels: ColumnData<FlowConsignadoListResponseDTO & { acao: string; }>[] = [
        { id: 'ordem', width: '5', key: 'ordem', header: 'Ordem', },
        { id: 'situacao', width: '5', key: 'situacao', header: 'Situação', },
        { id: 'cliente', width: '10', key: 'cliente', header: 'Cliente', },
        { id: 'vendedor', width: '10', key: 'vendedor', header: 'Vendedor', },
        { id: 'emissao', width: '5', key: 'emissao', header: 'Emissão', },
        { id: 'quantidadeConsignada', width: '5', key: 'quantidadeConsignada', header: 'Qtde. consignada', },
        {
            id: 'quantidadeDevolvido',
            width: '5',
            key: 'quantidadeDevolvido',
            header: 'Qtde. devolvida',
            convert: (item, { extraData }) => {
                let quantidade = item.quantidadeDevolvido - item.quantidadeVendida - item.quantidadeCancelada;
                if (quantidade < 0) quantidade = 0;
                return <TableCellWithTooltip
                    tooltipDisabled={true}
                    content={quantidade.toString()}
                    index={extraData.indexA}
                    tooltipPosition={extraData.indexA <= 4 ? 'bottom' : 'top'}
                    whiteSpace='nowrap'
                />;
            }
        },
        { id: 'saldo', width: '5', key: 'saldo', header: 'Saldo', },
        { id: 'quantidadeVendida', width: '5', key: 'quantidadeVendida', header: 'Qtde. vendida', },
        { id: 'quantidadeCancelada', width: '5', key: 'quantidadeCancelada', header: 'Qtde. cancelada', },
        { id: 'valorAberto', width: '5', key: 'valorAberto', header: 'Valor aberto', type: 'currency' },
        { id: 'valorConsignado', width: '5', key: 'valorConsignado', header: 'Valor consignado', type: 'currency' },
        {
            id: 'acao',
            width: '5',
            key: 'acao',
            header: '',
            convert: (item, { extraData }) => <Ellipsis
                index={extraData.indexA}
                onClick={onClickEllipsis}
                item={item}
                isValidButton={() => true}
            />
        },
    ];

    const footer: {
        key: keyof ConsignadoListTotalizadores;
        collspan?: number;
        justifyContent?: 'flex-start';
        convert?: (value: number) => string;
    }[] = [
            {
                key: 'quantidade',
                collspan: 5,
                justifyContent: 'flex-start',
                convert: (value: number) => `Quantidade: ${value}`
            },
            { key: 'quantidadeConsignada' },
            { key: 'quantidadeDevolvido' },
            { key: 'saldo' },
            { key: 'quantidadeVendida' },
            { key: 'quantidadeCancelada' },
            {
                key: 'valorAberto',
                convert: (value: number) => currencyOf(value),
            },
            {
                key: 'valorConsignado',
                convert: (value: number) => currencyOf(value),
            },
        ];

    useEffect(() => {
        if (!isFirstRenderRef.current) return;
        getClientesAc();
        getVendedoresAc();
        consultar();
        dispatch(resetConsignado());

        isFirstRenderRef.current = false;
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        consultar();
        // eslint-disable-next-line
    }, [
        localState.ordem,
        localState.cliente,
        localState.vendedor,
        localState.emissaoInicio,
        localState.emissaoFim,
        localState.situacao,
        localState.numeroDocumentoSaida,
        localState.numeroDocumentoEntrada,
        estabelecimentoSelecionado
    ]);

    useEffect(() => {
        dispatch(localState.isLoading ? loadingWithOpt({ loading: true, tipo: Tipo.CIRCULAR }) : completeLoading());
        setTimeout(() => {
            resetLoading();
        },);
        // eslint-disable-next-line
    }, [localState.isLoading]);

    const getTotalizadores = (itens: FlowConsignadoListResponseDTO[]): ConsignadoListTotalizadores => {
        return itens.reduce(
            (acc, curr) => {
                let quantidadeDevolvidaAtual = curr.quantidadeDevolvido - curr.quantidadeVendida - curr.quantidadeCancelada;
                if(quantidadeDevolvidaAtual < 0 ) quantidadeDevolvidaAtual = 0;
                return {
                    quantidade: acc.quantidade + 1,
                    quantidadeConsignada: acc.quantidadeConsignada + curr.quantidadeConsignada,
                    quantidadeDevolvido: acc.quantidadeDevolvido + quantidadeDevolvidaAtual,
                    saldo: acc.saldo + curr.saldo,
                    quantidadeVendida: acc.quantidadeVendida + curr.quantidadeVendida,
                    quantidadeCancelada: acc.quantidadeCancelada + curr.quantidadeCancelada,
                    valorAberto: acc.valorAberto + curr.valorAberto,
                    valorConsignado: acc.valorConsignado + curr.valorConsignado,
                };
            },
            {
                quantidade: 0,
                quantidadeConsignada: 0,
                quantidadeDevolvido: 0,
                saldo: 0,
                quantidadeVendida: 0,
                quantidadeCancelada: 0,
                valorAberto: 0,
                valorConsignado: 0,
            }
        );
    };

    const consultar = () => {
        setLocalState(prev => ({ ...prev, isLoading: true }));
        const {
            clientesAc,
            vendedoresAc,
            isLoading,
            itens,
            cliente,
            vendedor,
            ...rest
        } = localState;

        const filtro: FlowConsignadoListFilterPayloadDTO = {
            ...rest,
            cliente: cliente?.uuid ?? null,
            vendedor: vendedor?.uuid ?? null,
            emissaoInicio: toLocalDateBackEndFriendly(rest.emissaoInicio!),
            emissaoFim: toLocalDateBackEndFriendly(rest.emissaoFim!),
        };

        consignadoService.list(filtro)
            .then(({ data }) => {
                setLocalState(prev => ({ ...prev, itens: data.itens, qtdValores: data.quantidadesValores, totalizadores: getTotalizadores(data.itens) }));
            })
            .catch(() => { })
            .finally(() => {
                setLocalState(prev => ({ ...prev, isLoading: false }));
            });
    };

    const onClienteInputChange = (e: unknown, value: string = '') => {
        getClientesAc(value);
    };

    const getClientesAc = (toSearch: string = '') => {
        registraVendaService.getConsumidoresAc(toSearch).then(({ data }) => {
            setLocalState(prev => ({ ...prev, clientesAc: data }));
        }).catch(() => { });
    };

    const onVendedorInputChange = (e: unknown, value: string = '') => {
        getVendedoresAc(value);
    };

    const getVendedoresAc = (toSearch: string = '') => {
        registraVendaService.getVendedoresAc(toSearch).then(({ data }) => {
            setLocalState(prev => ({ ...prev, vendedoresAc: data }));
        }).catch(() => { });
    };

    const onClickEllipsis = (event: React.MouseEvent<HTMLButtonElement>, item: FlowConsignadoListResponseDTO) => {
        selectedItemref.current = { item, situacao: SituacaoConsignadoClass.of(item.situacao) };
        setAnchorEl(event.currentTarget);
    };

    const onCloseMenuRow = () => {
        setAnchorEl(null);
        setTimeout(() => {
            selectedItemref.current = null;
        }, 100);
    };

    const getSituacao = () => {
        return selectedItemref.current?.situacao;
    };
    const [visualizarDialog, setVisualizarDialog] = useState({
        open: false as boolean,
        uuidConsignado: null as string | null
    });

    const handleOpenVisualizarDialog = () => {
        const uuidConsignado = selectedItemref.current!.item.uuid;
        setVisualizarDialog({ open: true, uuidConsignado });
    };

    const handleColseVisualizarDialog = () => {
        setVisualizarDialog({ open: false, uuidConsignado: null });
        consultar();
    };

    const tableHeight = (props?: { calculateOnlyTableContentHeight?: boolean; subtract?: number; }) => {
        return `calc(100vh - 411px - ${(props?.calculateOnlyTableContentHeight ? 80 : 0) + (props?.subtract ?? 0)}px)`;
    };

    return (
        <Container
            maxWidth="xl"
            sx={{
                marginTop: '10px',
            }}
        >
            <Box
                sx={{
                    height: 'calc(100vh - 224px)',
                    display: 'grid',
                    gridTemplateRows: { md: `120px 50px auto 70px` },
                    gap: '5px',
                }}>

                <Box
                    id="cards-container"
                    sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: '15px',
                    }}>

                    <CardLocal
                        id="consignado"
                        label="Consignado"
                        qtd={localState.qtdValores.quantidadeConsignada}
                        valor={localState.qtdValores.valorConsignado}
                    />

                    <CardLocal
                        id="devolvido"
                        label="Devolvido"
                        qtd={localState.qtdValores.quantidadeDevolvida}
                        valor={localState.qtdValores.valorDevolvido}
                    />

                    <CardLocal
                        id="saldo"
                        label="Saldo"
                        qtd={localState.qtdValores.saldo}
                        valor={localState.qtdValores.valorSaldo}
                    />

                    <CardLocal
                        id="vendido"
                        label="Vendido"
                        qtd={localState.qtdValores.quantidadeVendida}
                        valor={localState.qtdValores.valorVendido}
                    />

                </Box>

                <Box
                    id="filtros-container"
                    sx={{
                        width: '100%',
                        height: '50px',
                        display: 'flex',
                        gap: '2%'
                    }}>

                    <FormControl
                        sx={{
                            width: '10%',
                            marginTop: '5px'
                        }}>
                        <NumericFormat
                            id={`ordem`}
                            decimalScale={0}
                            label="Ordem"
                            defaultValue={null}
                            customInput={TextField}
                            variant="standard"
                            allowNegative={false}
                            value={localState.ordem}
                            onValueChange={({ floatValue }) => {
                                setLocalState(prev => ({ ...prev, ordem: floatValue ?? null }));
                            }}
                        />
                    </FormControl>

                    <FormControl
                        sx={{
                            marginTop: '5px',
                            width: '30%',
                        }}>

                        <Autocomplete
                            value={localState.cliente}
                            id="clientes"
                            options={localState.clientesAc}
                            getOptionLabel={(option) => option.descritivo}
                            filterOptions={(x) => x}
                            noOptionsText={'Nenhum resultado encontrado'}
                            onInputChange={onClienteInputChange}
                            onChange={(_, value) => setLocalState(prev => ({ ...prev, cliente: value }))}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderInput={
                                (params) => <TextField {...params}
                                    label="Cliente"
                                    variant="standard"
                                />}
                        />
                    </FormControl>

                    <FormControl
                        sx={{
                            marginTop: '5px',
                            width: '30%',
                        }}>

                        <Autocomplete
                            value={localState.vendedor}
                            id="vendedor"
                            options={localState.vendedoresAc}
                            getOptionLabel={(option) => option.descritivo}
                            filterOptions={(x) => x}
                            noOptionsText={'Nenhum resultado encontrado'}
                            onInputChange={onVendedorInputChange}
                            onChange={(_, value) => setLocalState(prev => ({ ...prev, vendedor: value }))}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            renderInput={
                                (params) => <TextField {...params}
                                    label="Vendedor"
                                    variant="standard"
                                />}
                        />
                    </FormControl>

                    <Data
                        title="Emissão início"
                        dataValue={localState.emissaoInicio}
                        onChangeData={(key, data) => {
                            setLocalState(prev => ({ ...prev, [key]: data }));
                        }}
                        dataInicio={localState.emissaoInicio}
                        dataFim={localState.emissaoFim}
                        state='emissaoInicio'
                    />

                    <Data
                        title="Emissão fim"
                        dataValue={localState.emissaoFim}
                        onChangeData={(key, data) => {
                            setLocalState(prev => ({ ...prev, [key]: data }));
                        }}
                        dataInicio={localState.emissaoInicio}
                        dataFim={localState.emissaoFim}
                        state='emissaoFim'
                    />

                    <FormControl
                        sx={{
                            width: '20%', marginTop: '5px'
                        }}>
                        <InputLabel variant="standard" id="label-situacao" >Situação</InputLabel>
                        <Select id="select-situacao"
                            multiple
                            sx={{
                                width: '100%',
                            }}
                            variant="standard"
                            value={localState.situacao}
                            renderValue={(selected) => selected.join(', ')}
                            onChange={(e) => {
                                const value = (e.target.value ?? []) as SituacaoConsignado[];
                                if (!value.length) {
                                    value.push(...initialState.situacao);
                                }
                                setLocalState(prev => ({ ...prev, situacao: value }));
                            }}
                        >
                            {getEnumObjNameDescritivo(SituacaoConsignado, SituacaoConsignado)
                                .filter(s => {
                                    return [
                                        SituacaoConsignado.ABERTO,
                                        SituacaoConsignado.PARCIAL,
                                        SituacaoConsignado.CONSIGNADO,
                                        SituacaoConsignado.CANCELADO,
                                        SituacaoConsignado.FECHADO
                                    ].includes(s.name as SituacaoConsignado);
                                })
                                .map((item, i) => {
                                    const { label, name } = item;
                                    return (
                                        <MenuItem
                                            key={i}
                                            value={label}
                                            id={`menu_item_situacao_${label}`}
                                        >
                                            <ListItemText primary={label} />
                                            <Checkbox
                                                sx={{ color: theme.palette.primary.main }}
                                                checked={localState.situacao.indexOf(name as SituacaoConsignado) !== -1}
                                                color='primary'
                                            />
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </FormControl>

                    <FormControl
                        sx={{
                            width: '20rem',
                            marginTop: '5px'
                        }}>
                        <NumericFormat
                            id={`numero-documento-saida`}
                            decimalScale={0}
                            label="Documento de saída"
                            defaultValue={null}
                            customInput={TextField}
                            variant="standard"
                            allowNegative={false}
                            value={localState.numeroDocumentoSaida}
                            onValueChange={({ floatValue }) => {
                                setLocalState(prev => ({ ...prev, numeroDocumentoSaida: floatValue ?? null }));
                            }}
                        />
                    </FormControl>

                    <FormControl
                        sx={{
                            width: '20rem',
                            marginTop: '5px'
                        }}>
                        <NumericFormat
                            id={`numero-documento-entrada`}
                            decimalScale={0}
                            label="Documento de entrada"
                            defaultValue={null}
                            customInput={TextField}
                            variant="standard"
                            allowNegative={false}
                            value={localState.numeroDocumentoEntrada}
                            onValueChange={({ floatValue }) => {
                                setLocalState(prev => ({ ...prev, numeroDocumentoEntrada: floatValue ?? null }));
                            }}
                        />
                    </FormControl>

                </Box>

                <Box>
                    <TableContainer sx={{
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 1,
                        position: 'relative',
                        boxSizing: 'border-box',
                        height: tableHeight(),
                        marginTop: '10px',
                    }}
                        className="color-orange">

                        <Table
                            id="content-table"
                            stickyHeader
                            size="small"
                            key="content-table"
                        >
                            <TableHead key="header_table" id="header_table">
                                <TableRow key="header_row_table" id="header_row_table">
                                    {relatorioColumnsLabels.map((header) => (
                                        <TableCell
                                            sx={{ whiteSpace: 'nowrap', width: `${header.width}%`, textAlign: 'center', fontWeight: 'bold' }}
                                            key={`header_cell_table_${header.id}`}
                                            id={`header_cell_table_${header.id}`}
                                        >
                                            {header.header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody
                                sx={{
                                    overflowY: 'auto',
                                    // não questione... apenas aceite...
                                    '&::after': {
                                        content: '""',
                                        display: 'block',
                                        height: tableHeight({
                                            calculateOnlyTableContentHeight: true,
                                            subtract: localState.itens.length * 38
                                        }),
                                        marginTop: 'auto',
                                    }
                                }}
                            >
                                {
                                    localState.itens.map((item, indexA) => {

                                        return (
                                            <TableRow
                                                key={item.uuid.toString()}
                                            >
                                                {relatorioColumnsLabels.map((a, indexB) => {
                                                    let value: any = item[a.key as keyof FlowConsignadoListResponseDTO];
                                                    if (a.type === 'currency') value = currencyOf(value);

                                                    return (
                                                        <TableCell
                                                            sx={{
                                                                textAlign: 'center', position: 'relative'
                                                            }} id={`item_${indexB + 1}_row_${indexA}_${a.id}`}
                                                            key={`tableCell_${a.key}_${indexB}`}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    height: '25px',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                {
                                                                    a.convert?.(item as FlowConsignadoListResponseDTO & { acao: string; }, { column: a, extraData: { indexA } })
                                                                    ?? <TableCellWithTooltip
                                                                        tooltipDisabled={!['cliente', 'vendedor'].includes(a.key)}
                                                                        content={value?.toString() ?? ''}
                                                                        index={indexA}
                                                                        tooltipPosition={indexA <= 4 ? 'bottom' : 'top'}
                                                                        whiteSpace='nowrap'
                                                                    />
                                                                }
                                                            </Box>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );

                                    })
                                }

                                {!localState.itens.length &&
                                    <TableRow
                                        key="sem_consignado_table_row_error"
                                    >
                                        <TableCell
                                            key="row_sem_consignado_table_cell_error"
                                            colSpan={999}
                                            sx={{
                                                fontSize: '48px',
                                                color: customColors.cinzaClaro,
                                                padding: 0,
                                                userSelect: 'none',
                                            }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: tableHeight({ calculateOnlyTableContentHeight: true }),
                                                width: '100%'
                                            }}>
                                                Não há consignados para os filtros informados.
                                            </Box>
                                        </TableCell>
                                    </TableRow>}

                                <Menu key="menu_row"
                                    anchorEl={anchorEl}
                                    open={openMenuRow}
                                    onClose={onCloseMenuRow}
                                >
                                    <MenuList>

                                        {getSituacao()?.permiteGerenciar() &&
                                            <MenuItem
                                                disabled={!caixaAberto || !canEditar}
                                                key="menu-item-gerenciar"
                                                onClick={() => { onCloseMenuRow(); navigate("/registra-consignado", { state: { uuid: selectedItemref.current?.item.uuid } }); }}
                                            >
                                                <ListItemIcon>
                                                    <Icon className="fa-solid fa-pen" sx={{ fontSize: "18px", width: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText>Gerenciar consignado</ListItemText>
                                            </MenuItem>
                                        }

                                        <MenuItem key="menu-item-imprimir" disabled={!canImprimir} onClick={() => {
                                            onCloseMenuRow();
                                            consignadoImpressaoService.impressao(selectedItemref.current!.item.uuid)
                                                .then(({ data }) => {
                                                    setImpressao(data);
                                                });
                                        }}>
                                            <ListItemIcon>
                                                <Icon className="fa-solid fa-print" sx={{ fontSize: "18px", width: '20px' }} />
                                            </ListItemIcon>
                                            <ListItemText>Imprimir</ListItemText>
                                        </MenuItem>

                                        <MenuItem key="menu-item-movimentacoes" disabled={!canMovimentacoes} onClick={() => { handleOpenVisualizarDialog(); onCloseMenuRow(); }}>
                                            <ListItemIcon>
                                                <Icon className="fa-solid fa-file-invoice-dollar" sx={{ fontSize: "18px", width: '20px' }} />
                                            </ListItemIcon>
                                            <ListItemText>Movimentações</ListItemText>
                                        </MenuItem>


                                        {
                                            (getSituacao()?.possuiConsignado()) && <>
                                                <MenuItem key="menu-item-devolucao"
                                                    disabled={!caixaAberto || !canDevolucaoAcoes}
                                                    onClick={() => {
                                                        onCloseMenuRow();
                                                        navigate("/devolucao-consignado", {
                                                            state: {
                                                                uuid: selectedItemref.current!.item.uuid,
                                                                cliente: selectedItemref.current!.item.cliente,
                                                                vendedor: selectedItemref.current!.item.vendedor,
                                                                origem: 'list',
                                                            }
                                                        });
                                                    }}>
                                                    <ListItemIcon>
                                                        <Icon className="fa-solid fa-reply" sx={{ fontSize: "18px", width: '20px' }} />
                                                    </ListItemIcon>
                                                    <ListItemText>Devolucão</ListItemText>
                                                </MenuItem>

                                                <MenuItem key="menu-item-gerar-venda"
                                                    disabled={!caixaAberto || !canGerarVendaAcoes}
                                                    onClick={() => {
                                                        onCloseMenuRow();
                                                        showProgress({
                                                            mensagens: {
                                                                0: 'Devolvendo itens',
                                                                50: 'Gerando venda',
                                                            },
                                                        });

                                                        consignadoService.gerarVenda(selectedItemref.current!.item.uuid)
                                                            .then(({ data }) => {
                                                                vendaService.carregaPedido(data.uuid)
                                                                    .then(({ data }) => {
                                                                        if (data.statusPedido === StatusPedido.ABERTO.toString().toUpperCase()) {
                                                                            if (data.tabelaPreco) dispatch(setTabelaPreco(data.tabelaPreco));
                                                                            if (data.deposito) dispatch(setDeposito(data.deposito));
                                                                            if (data.transportador) dispatch(setTransportador(data.transportador));
                                                                            data.itens.forEach(e => e.estoque = -1);
                                                                            dispatch(setStateByProps(data));
                                                                            dispatch(setPagamentos({ itens: [], itensTroca: [] }));
                                                                            setTimeout(() => {
                                                                                addSuccess({ message: 'Venda gerada com sucesso. Redirecionando para o PDV!' });
                                                                                navigate("/registra-venda", { state: true });
                                                                            }, 200);
                                                                            return;
                                                                        }
                                                                    })
                                                                    .catch(() => {
                                                                        addError({ message: "Sistema temporariamente indisponivel. Se o erro persistir, contate o suporte", closeable: true, persist: true, id: "sistema-indisponivel-error", preventDuplicate: true });
                                                                    });
                                                            })
                                                            .catch(err => {
                                                                const problema: Problem = err.response.data;

                                                                if (problema.code === 'BUSINESS_EXCEPTION' && problema.detail === 'validateSituacoesNotasSaida') {
                                                                    dialog.confirm({
                                                                        error: true,
                                                                        title: 'Não é possível gerar venda',
                                                                        message: <ValidateSituacaoNotasSaidasDevolucao problema={problema} />,
                                                                        onConfirm: () => { },
                                                                    });
                                                                } else {
                                                                    throwErrorMessage(err);
                                                                }
                                                            })
                                                            .finally(() => {
                                                                setTimeout(() => {
                                                                    hideProgress();
                                                                }, 300);
                                                            });
                                                    }}>
                                                    <ListItemIcon>
                                                        <Icon className="fa-solid fa-cart-arrow-down" sx={{ fontSize: "18px", width: '20px' }} />
                                                    </ListItemIcon>
                                                    <ListItemText>Gerar venda</ListItemText>
                                                </MenuItem>
                                            </>
                                        }


                                        {getSituacao()?.permiteCancelar() &&
                                            <MenuItem key="menu-item-cancelar" disabled={!canCancelar} onClick={() => {
                                                onCloseMenuRow();
                                                setShowCancelarDialog(true);
                                            }}>
                                                <ListItemIcon>
                                                    <Icon className="fa-solid fa-trash" sx={{ fontSize: "18px", width: '20px' }} />
                                                </ListItemIcon>
                                                <ListItemText>Cancelar</ListItemText>
                                            </MenuItem>
                                        }
                                    </MenuList>
                                </Menu>
                            </TableBody>

                            {!!localState.itens.length &&
                                <TableFooter
                                    sx={{
                                        width: '100%',
                                        position: 'sticky',
                                        bottom: 0,
                                        height: '40px',
                                    }} className='glassmorphism'>
                                    <TableRow sx={{
                                        width: '100%',
                                    }}>
                                        {footer.map(item => {
                                            const value = localState.totalizadores[item.key];
                                            return <TableCell
                                                key={item.key}
                                                id={item.key}
                                                colSpan={item.collspan}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: theme.palette.common.black,
                                                    fontSize: '14px',
                                                    border: 0,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: item.justifyContent ?? 'center',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                    {item.convert?.(value) ?? value}
                                                </Box>
                                            </TableCell>;
                                        })}
                                    </TableRow>
                                </TableFooter>
                            }
                        </Table>
                    </TableContainer>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center' }}>
                    <CustomButton
                        id="novo"
                        onClick={() => { navigate("/registra-consignado"); }}
                        variant="contained"
                        color="success"
                        disabled={!caixaAberto || !canNovo}
                        startIcon={<Icon className='fa-solid fa-arrow-right' />}>
                        Ir para o consignado
                    </CustomButton>
                </Box>

            </Box>
            <ConsignadoVisualizarDialog {...visualizarDialog} onCloseVisualizarDialog={handleColseVisualizarDialog} />

            {!!impressao &&
                <ConsignadoPDF
                    dados={impressao}
                    onClose={() => {
                        setImpressao(null);
                    }}
                />
            }

            <CancelaCoisasDialog
                header="Deseja cancelar o consignado?"
                open={showCancelarDialog}
                onClose={() => {
                    setShowCancelarDialog(false);
                }}
                onConfirm={(item, uuidMotivo) => {
                    setLocalState(prev => ({ ...prev, isLoading: true }));
                    consignadoService.cancelar(item.uuid, uuidMotivo)
                        .then(() => {
                            consultar();
                            addSuccess({ message: 'Consignado cancelado com sucesso!' });
                        })
                        .catch(err => {
                            throwErrorMessage(err);
                            setLocalState(prev => ({ ...prev, isLoading: true }));
                        });
                }}
                item={selectedItemref.current?.item}
                columns={[
                    {
                        id: 'ordem',
                        header: 'Ordem',
                        key: 'ordem',
                        width: 'auto',
                    },

                    {
                        id: 'emissao',
                        header: 'Emissão',
                        key: 'emissao',
                        width: 'auto',
                    },

                    {
                        id: 'cliente',
                        header: 'Cliente',
                        key: 'cliente',
                        width: 'auto',
                    },

                    {
                        id: 'situacao',
                        header: 'Situação',
                        key: 'situacao',
                        width: 'auto',
                    },
                ]}
            />
        </Container>
    );
};

export default ConsignadoList;


const CardLocal = ({ id, label, qtd, valor }: { id: string, label: string, qtd: number, valor: number; }) => {
    return <Card
        id={`${id}-card`}
        sx={{
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
        }}
    >

        <Typography
            id={`${id}-label`}
            sx={{
                fontSize: '18px',
                color: 'text.secondary',
                fontWeight: 'bold'
            }}
        >
            {label}
        </Typography>
        <Box
            id={`${id}-qtd-wrapper`}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '80%',
            }}
        >
            <Typography
                id={`${id}-qtd-label`}
                sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.disabled,
                    fontSize: '14px'
                }}>
                Quantidade total:
            </Typography>
            <Typography
                id={`${id}-qtd`}
                sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    fontSize: '18px'
                }}
            >
                {qtd}
            </Typography>
        </Box>

        <Box
            id={`${id}-valor-wrapper`}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '80%',
            }}
        >
            <Typography
                id={`${id}-valor-label`}
                sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.disabled,
                    fontSize: '14px'
                }}>
                Valor total:
            </Typography>
            <Typography
                id={`${id}-valor`}
                sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    fontSize: '18px'
                }}
            >
                {currencyOf(valor)}
            </Typography>
        </Box>


    </Card>;
};
